import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Camera, CheckCircle2, Loader2, RefreshCw,
  Shield, ShieldCheck, ShieldAlert, Upload, X, Lock,
} from "lucide-react";

const MAX_DIM = 1280;

async function fileToScaledBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const { width, height } = img;
        const scale = Math.min(1, MAX_DIM / Math.max(width, height));
        const w = Math.round(width * scale);
        const h = Math.round(height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function VerifyPage() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(1);
  const [idPhoto, setIdPhoto] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/verify/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  if (user?.verified) {
    return <AlreadyVerified onContinue={() => navigate("/app")} />;
  }

  const pickId = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const data = await fileToScaledBase64(f);
      setIdPhoto(data);
      setStep(2);
    } catch {
      toast.error("Could not read that image");
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/verify/submit", {
        id_photo_base64: idPhoto,
        selfie_base64: selfie,
      });
      setResult(data);
      setStep(4);
      if (data.approved) await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed — try again");
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setIdPhoto(null);
    setSelfie(null);
    setResult(null);
    setStep(1);
  };

  return (
    <div className="min-h-screen grain-bg flex flex-col" data-testid="verify-page">
      <header className="px-6 sm:px-10 py-5 flex items-center justify-between">
        <button data-testid="verify-back" onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#737373] hover:text-[#1A1818]">
          <ArrowLeft size={16} /> Back
        </button>
        <span className="font-serif italic text-2xl text-[#913831]">rendezvous</span>
        <div className="flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-[#737373]">
          <Lock size={11} /> Private
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 sm:px-10 pb-20">
        <div className="mb-8 p-5 rounded-3xl bg-gradient-to-br from-[#913831] to-[#7A2E28] text-white relative overflow-hidden" data-testid="verify-stats-banner">
          <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-[#D4A373]/20 blur-2xl" />
          <div className="relative flex items-center gap-4">
            <div className="text-5xl sm:text-6xl font-serif italic text-[#D4A373]">3&times;</div>
            <div>
              <div className="font-serif text-xl sm:text-2xl leading-tight">Verified profiles get <em>three times</em> more matches.</div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/70 mt-1">Internal data &middot; 2026 cohort</div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#913831]">
          <Shield size={12} /> Identity check &middot; Step 0{Math.min(step, 4)}/04
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#1A1818] mt-3 leading-tight">
          Prove you&apos;re <em className="text-[#913831]">you</em>.
        </h1>
        <p className="mt-3 text-[#737373] max-w-md">
          Two quick photos. Claude compares them, then deletes both. We only keep
          the verdict — never your ID.
        </p>

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.section key="s1" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="mt-10">
              <h2 className="font-serif text-2xl text-[#1A1818]">1. Photograph your ID.</h2>
              <p className="text-sm text-[#737373] mt-2">
                Driver&apos;s license, passport, or national ID. Lay it flat, good light, no glare.
              </p>
              <label
                data-testid="id-upload-zone"
                className="mt-6 block cursor-pointer">
                <input
                  ref={fileRef}
                  data-testid="id-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  capture="environment"
                  hidden
                  onChange={pickId}
                />
                <div className="aspect-[16/10] rounded-3xl border-2 border-dashed border-[#E5DFD3] hover:border-[#913831] hover:bg-[#F0EBE1]/40 flex flex-col items-center justify-center text-[#737373] transition-colors">
                  <Upload size={28} strokeWidth={1.25} />
                  <div className="text-sm uppercase tracking-[0.2em] mt-3">Tap to capture / upload ID</div>
                  <div className="text-xs mt-1 text-[#A09A8B]">JPEG / PNG / WEBP &middot; under 6 MB</div>
                </div>
              </label>
              <div className="mt-5 text-xs text-[#737373]">
                <ShieldCheck size={12} className="inline mr-1 text-[#913831]" />
                Your ID is sent encrypted to our server, compared by Claude, and immediately deleted.
              </div>
            </motion.section>
          )}

          {step === 2 && (
            <motion.section key="s2" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="mt-10">
              <h2 className="font-serif text-2xl text-[#1A1818]">2. Confirm the ID looks right.</h2>
              <div className="mt-4 rounded-3xl overflow-hidden border border-[#E5DFD3] bg-white p-3">
                <img src={idPhoto} alt="" className="w-full rounded-2xl" data-testid="id-preview" />
              </div>
              <div className="mt-6 flex justify-between">
                <button data-testid="retake-id" onClick={() => { setIdPhoto(null); setStep(1); }}
                  className="text-sm text-[#737373] hover:text-[#1A1818] inline-flex items-center gap-1">
                  <RefreshCw size={14} /> Retake
                </button>
                <button data-testid="goto-selfie" onClick={() => setStep(3)}
                  className="inline-flex items-center gap-2 bg-[#913831] hover:bg-[#7A2E28] text-white rounded-full px-6 py-3 font-medium">
                  Continue to selfie <ArrowRight size={16} />
                </button>
              </div>
            </motion.section>
          )}

          {step === 3 && (
            <SelfieStep
              onCapture={(s) => setSelfie(s)}
              selfie={selfie}
              onSubmit={submit}
              submitting={submitting}
              onRestart={restart}
            />
          )}

          {step === 4 && result && (
            <VerdictView result={result} onRetry={restart} onContinue={() => navigate("/app")} />
          )}
        </AnimatePresence>

        {status?.latest && step === 1 && !status.verified && (
          <div className="mt-10 p-5 rounded-2xl bg-white border border-[#E5DFD3]" data-testid="prev-attempt">
            <div className="text-xs uppercase tracking-[0.2em] text-[#737373]">Previous attempt</div>
            <div className="text-sm text-[#1A1818] mt-1 font-serif italic">
              &ldquo;{status.latest.reason || "Not approved."}&rdquo;
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function SelfieStep({ onCapture, selfie, onSubmit, submitting, onRestart }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 960 }, height: { ideal: 960 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setStarting(false);
      } catch (e) {
        setError("Camera access denied. Allow camera permission in your browser, or upload a selfie instead.");
        setStarting(false);
      }
    };
    if (!selfie) start();
    else setStarting(false);
    return () => {
      cancelled = true;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfie]);

  const capture = () => {
    const v = videoRef.current;
    if (!v) return;
    const w = Math.min(v.videoWidth || 720, MAX_DIM);
    const h = Math.min(v.videoHeight || 720, MAX_DIM);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.translate(w, 0); ctx.scale(-1, 1); // mirror back to natural orientation
    ctx.drawImage(v, 0, 0, w, h);
    onCapture(canvas.toDataURL("image/jpeg", 0.85));
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  };

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const data = await fileToScaledBase64(f);
    onCapture(data);
  };

  return (
    <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
      className="mt-10">
      <h2 className="font-serif text-2xl text-[#1A1818]">3. A live selfie.</h2>
      <p className="text-sm text-[#737373] mt-2">
        Face the camera, remove sunglasses/hats. We&apos;ll compare it to your ID and discard both.
      </p>

      {!selfie && !error && (
        <div className="mt-6 relative aspect-square max-w-md mx-auto rounded-3xl overflow-hidden border border-[#E5DFD3] bg-[#1A1818]" data-testid="selfie-camera">
          {starting && (
            <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
              <Loader2 size={20} className="animate-spin mr-2" /> Starting camera…
            </div>
          )}
          <video ref={videoRef} className="w-full h-full object-cover scale-x-[-1]"
                 autoPlay playsInline muted data-testid="selfie-video" />
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="w-3/5 h-3/5 rounded-full border-2 border-dashed border-white/60" />
          </div>
        </div>
      )}

      {error && (
        <div className="mt-6 p-5 rounded-2xl bg-[#FFF4F1] border border-[#D9534F]/30 text-sm text-[#1A1818]" data-testid="camera-error">
          <ShieldAlert size={16} className="inline mr-1 text-[#D9534F]" /> {error}
          <div className="mt-4">
            <label className="inline-flex items-center gap-2 bg-[#913831] hover:bg-[#7A2E28] text-white rounded-full px-5 py-2.5 text-sm font-medium cursor-pointer"
                   data-testid="selfie-upload-fallback">
              <Upload size={14} /> Upload a selfie instead
              <input type="file" accept="image/*" hidden onChange={handleUpload} />
            </label>
          </div>
        </div>
      )}

      {selfie && (
        <div className="mt-6 aspect-square max-w-md mx-auto rounded-3xl overflow-hidden border border-[#E5DFD3] bg-white p-3">
          <img src={selfie} alt="" className="w-full h-full object-cover rounded-2xl" data-testid="selfie-preview" />
        </div>
      )}

      <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-between items-stretch sm:items-center">
        <button data-testid="restart-verify" onClick={onRestart}
          className="text-sm text-[#737373] hover:text-[#1A1818] inline-flex items-center gap-1 justify-center">
          <X size={14} /> Start over
        </button>
        {!selfie && !error && (
          <button data-testid="selfie-capture" onClick={capture} disabled={starting}
            className="inline-flex items-center gap-2 bg-[#913831] hover:bg-[#7A2E28] disabled:opacity-50 text-white rounded-full px-6 py-3.5 font-medium">
            <Camera size={16} /> Capture
          </button>
        )}
        {selfie && (
          <div className="flex gap-3">
            <button data-testid="selfie-retake" onClick={() => onCapture(null)}
              className="border border-[#E5DFD3] hover:bg-[#F0EBE1] rounded-full px-5 py-3 text-sm inline-flex items-center gap-1">
              <RefreshCw size={14} /> Retake
            </button>
            <button data-testid="verify-submit" onClick={onSubmit} disabled={submitting}
              className="inline-flex items-center gap-2 bg-[#913831] hover:bg-[#7A2E28] disabled:opacity-50 text-white rounded-full px-6 py-3.5 font-medium">
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {submitting ? "Claude is checking…" : "Submit for verification"}
            </button>
          </div>
        )}
      </div>
    </motion.section>
  );
}

function VerdictView({ result, onRetry, onContinue }) {
  const ok = result.approved;
  return (
    <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="mt-10 text-center" data-testid="verify-verdict">
      <motion.div initial={{ scale: 0.6 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 110 }}
        className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center ${
          ok ? "bg-[#F3E8DD] border border-[#D4A373]/40" : "bg-[#FFF4F1] border border-[#D9534F]/30"
        }`}>
        {ok
          ? <ShieldCheck size={32} className="text-[#913831]" strokeWidth={1.5} />
          : <ShieldAlert size={32} className="text-[#D9534F]" strokeWidth={1.5} />}
      </motion.div>
      <h3 className="font-serif text-4xl mt-6 text-[#1A1818]" data-testid="verdict-headline">
        {ok ? "You're verified." : "Not approved."}
      </h3>
      <p className="mt-3 text-[#737373] max-w-md mx-auto font-serif italic" data-testid="verdict-reason">
        &ldquo;{result.reason}&rdquo;
      </p>
      <div className="mt-4 inline-flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-[#737373]">
        <span>Confidence: <span className="text-[#1A1818] not-uppercase tracking-normal font-serif italic">{result.confidence}</span></span>
      </div>
      <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
        {ok ? (
          <button data-testid="verdict-continue" onClick={onContinue}
            className="bg-[#913831] hover:bg-[#7A2E28] text-white rounded-full px-7 py-3.5 font-medium">
            Back to rendezvous
          </button>
        ) : (
          <>
            <button data-testid="verdict-retry" onClick={onRetry}
              className="bg-[#913831] hover:bg-[#7A2E28] text-white rounded-full px-7 py-3.5 font-medium">
              Try again
            </button>
            <button data-testid="verdict-skip" onClick={onContinue}
              className="border border-[#E5DFD3] hover:bg-[#F0EBE1] rounded-full px-7 py-3.5">
              Skip for now
            </button>
          </>
        )}
      </div>
    </motion.section>
  );
}

function AlreadyVerified({ onContinue }) {
  return (
    <div className="min-h-screen grain-bg flex items-center justify-center px-6" data-testid="already-verified">
      <div className="text-center">
        <ShieldCheck size={42} strokeWidth={1.5} className="text-[#913831] mx-auto" />
        <h1 className="font-serif text-4xl mt-5">You&apos;re already verified.</h1>
        <p className="text-[#737373] mt-2">Your profile carries the verified badge.</p>
        <button onClick={onContinue} data-testid="continue-app"
          className="mt-7 bg-[#913831] hover:bg-[#7A2E28] text-white rounded-full px-7 py-3.5 font-medium">
          Back to rendezvous
        </button>
      </div>
    </div>
  );
}
